import { supabase } from '@/integrations/supabase/client';

interface Draft {
  id: string;
  title: string;
  region: string;
  content: string;
  source_articles: any[];
  event_clusters: any[];
  composite_score: number;
  generation_metadata: any;
}

// Region photo URL mapping - matches region-config.ts photoUrl values
const REGION_PHOTO_MAP: Record<string, string> = {
  'europa': '/images/europa-map.png',
  'nord_amerika': '/images/nord-amerika.png',
  'asia': '/images/asia-map.png',
  'latin_amerika': '/images/latin-america-map.png',
  'midtosten': '/images/middle-east-map.png',
  'afrika': '/images/africa-map-new.png',
  'skandinavia': '/images/scandinavia-map.png',
};

// Region context text mapping - dynamic context based on region
const REGION_CONTEXT_MAP: Record<string, string> = {
  'europa': 'Her er en oppsummering av de viktigste hendelsene i Europa forrige uke, basert på analyser fra flere nyhetskilder.',
  'nord_amerika': 'Her er en oppsummering av de viktigste hendelsene i Nord-Amerika forrige uke, basert på analyser fra flere nyhetskilder.',
  'asia': 'Her er en oppsummering av de viktigste hendelsene i Asia forrige uke, basert på analyser fra flere nyhetskilder.',
  'latin_amerika': 'Her er en oppsummering av de viktigste hendelsene i Latin-Amerika forrige uke, basert på analyser fra flere nyhetskilder.',
  'midtosten': 'Her er en oppsummering av de viktigste hendelsene i Midtøsten forrige uke, basert på analyser fra flere nyhetskilder.',
  'afrika': 'Her er en oppsummering av de viktigste hendelsene i Afrika forrige uke, basert på analyser fra flere nyhetskilder.',
  'skandinavia': 'Her er en oppsummering av de viktigste hendelsene i Skandinavia forrige uke, basert på analyser fra flere nyhetskilder.',
};

/**
 * Approve a draft and publish it as an interview
 */
export async function approveDraft(draftId: string, draft: Draft): Promise<void> {
  // Get photo URL from region mapping
  const photoUrl = REGION_PHOTO_MAP[draft.region] || '/images/asia-map.png';
  
  // Get context text from region mapping
  const contextText = REGION_CONTEXT_MAP[draft.region] || 'Her er en oppsummering av de viktigste hendelsene forrige uke, basert på analyser fra flere nyhetskilder.';
  
  console.log(`[approveDraft] Using photo URL: ${photoUrl} and context: ${contextText} for region: ${draft.region}`);
  
  // Step 1: Create the interview from the draft
  const { data: interview, error: insertError } = await supabase
    .from('interviews')
    .insert([{
      title: draft.title,
      interviewee_name: 'Kai',
      job_title: 'KI-journalist',
      company: 'Briefr',
      context: contextText,
      question: draft.title,
      answer: draft.content,
      tags: [draft.region, 'briefr-bullets', 'ai-generated', 'rag'],
      article_type: 'brief_bullets' as const,
      publication_status: 'published',
      is_in_feeder: false,
      timestamp: new Date().toISOString(),
      photo_url: photoUrl, // Use the resolved photo URL from region mapping
      source_articles: draft.source_articles, // Copy source articles for transparency
    }])
    .select()
    .single();

  if (insertError) {
    throw new Error(`Failed to publish interview: ${insertError.message}`);
  }

  // Step 2: Update draft status to 'published' and link to the interview
  const { error: updateError } = await supabase
    .from('ai_article_drafts')
    .update({
      status: 'published',
      published_at: new Date().toISOString(),
      published_interview_id: interview.id,
      updated_at: new Date().toISOString(),
    })
    .eq('id', draftId);

  if (updateError) {
    throw new Error(`Failed to update draft status: ${updateError.message}`);
  }

  // Step 3: Update region_publish_metadata
  const { error: metadataError } = await supabase
    .from('region_publish_metadata')
    .update({
      last_published_at: new Date().toISOString(),
    })
    .eq('region', draft.region);

  if (metadataError) {
    console.error('Failed to update region metadata:', metadataError);
    // Don't throw - this is not critical
  }
}

/**
 * Reject a draft with a reason
 */
export async function rejectDraft(draftId: string, rejectionReason: string): Promise<void> {
  const { error } = await supabase
    .from('ai_article_drafts')
    .update({
      status: 'rejected',
      rejection_reason: rejectionReason,
      updated_at: new Date().toISOString(),
    })
    .eq('id', draftId);

  if (error) {
    throw new Error(`Failed to reject draft: ${error.message}`);
  }
}
